import {makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import { GeocodedSuburb } from '../models/middleware';
import apiService from '../transport/apiService';
import {RewardsSignUpResponse} from '../transport/types';
import {
  getValueFromStorage,
  removeValueFromStorage,
  setValueToStorage,
} from '../utils/localStorage';

import type {RootStore} from './rootStore';
const REWARDS_STORE_KEY = 'REWARDS_STORE';

// These are the properties that are needed to show
// the sign up success screen. Can modify as needs change
type RewardsStorePersist = Pick<
  RewardsStore,
  | 'hasRewards'
  | 'cardNumber'
  | 'firstName'
  | 'lastName'
  | 'shortcode'
  | 'shortcodeExpiresAt'
  | 'walletUrl'
>;

// This store is for the rewards data which ultimately lives in Zen

async function rewardsSetValueToStorage(data: RewardsStorePersist) {
  try {
    await setValueToStorage(REWARDS_STORE_KEY, JSON.stringify(data));
  } catch (e) {
    throw new Error(`Failed setValueToStorage ${REWARDS_STORE_KEY}`);
  }
}

async function rewardsGetValueFromStorage() {
  try {
    const data = await getValueFromStorage(REWARDS_STORE_KEY);
    if (data) {
      return JSON.parse(data) as RewardsStorePersist;
    }
  } catch (e) {
    throw new Error(`Failed getValueFromStorage ${REWARDS_STORE_KEY}`);
  }
  return null;
}

export class RewardsStore {
  rootStore: RootStore;

  hasRewards = false;

  firstName: string = '';
  lastName: string = '';

  /**
   * Starts with +61
   */
  phoneNumber: string = '';
  emailAddress: string = '';
  dateOfBirth: string = ''; // YYYY-MM-DD format
  suburb: Pick<GeocodedSuburb, 'locality' | 'postal_code' | 'administrative_area'> | null = null;

  cardNumber: string = '';
  shortcode: string = '';
  shortcodeExpiresAt: string = '';
  walletUrl: string = '';

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);
    runInAction(() => {
      this.load();
    });
  }

  dump = async () => {
    await rewardsSetValueToStorage({
      hasRewards: this.hasRewards,
      firstName: this.firstName,
      lastName: this.lastName,
      cardNumber: this.cardNumber,
      shortcode: this.shortcode,
      shortcodeExpiresAt: this.shortcodeExpiresAt,
      walletUrl: this.walletUrl,
    });
  };

  load = async () => {
    const data = await rewardsGetValueFromStorage();
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        (this[key as keyof RewardsStorePersist] as any) = value;
      });
    }
  };

  /**
   * Starts with +61
   */
  setPhoneNumber = (value: string) => {
    if (value?.indexOf('0') === 0) {
      value = '+61' + value.substring(1);
    }

    this.phoneNumber = value;
  };

  setFirstName = (value: string) => {
    this.firstName = value;
  };

  setLastName = (value: string) => {
    this.lastName = value;
  };

  setEmailAddress = (value: string) => {
    this.emailAddress = value;
  };

  setDateOfBirth = (value: string) => {
    this.dateOfBirth = value;
  };

  setSuburb = (value: Pick<GeocodedSuburb, 'locality' | 'postal_code' | 'administrative_area'>) => {
    this.suburb = value;
  };

  setCardNumber = (value: string) => {
    this.cardNumber = value;
  };

  setShortcode = (value: string) => {
    this.shortcode = value;
  };

  setShortcodeExpiresAt = (value: string) => {
    this.shortcodeExpiresAt = value;
  };

  setWalletUrl = (value: string) => {
    this.walletUrl = value;
  };

  loadRewards = (data: RewardsSignUpResponse) => {
    runInAction(() => {
      this.hasRewards = true;
      this.firstName = data.firstName;
      this.lastName = data.lastName;
      this.cardNumber = data.cardNumber;
      this.shortcode = data.shortcode;
      this.shortcodeExpiresAt = data.shortcodeExpiresAt;
      this.walletUrl = data.walletUrl;
      this.dump();
    });
  };

  loadRewardsForUser = async (phoneNumber: string) => {
    // user is authenticated with mfl credentials
    const result = await apiService.rewardsGetAccount({
      phoneNumber,
    });

    if (result?.status === 200) {
      runInAction(() => {
        this.hasRewards = result.data?.exists || false;
        this.cardNumber = result.data?.cardNumber;
        this.shortcode = result.data?.shortcode;
        this.shortcodeExpiresAt = result.data?.shortcodeExpiresAt;
        this.walletUrl = result.data?.walletUrl;
        this.dump();
      });
    }
  };

  optOut = async (phoneNumber: string, lastName: string) => {
    const result = await apiService.rewardsOptOut({
      phoneNumber,
      lastName,
    });

    if (result) {
      runInAction(() => {
        this.reset();
      });
      // reload the cart to remove rewards pricing to existing cart items
      this.rootStore.cartStore.fullyReloadOrderFromMFL();

      // offers store is no longer valid after opting out
      this.rootStore.resetOffersStore();
    }
  };

  reset = () => {
    this.hasRewards = false;
    this.firstName = '';
    this.lastName = '';
    this.phoneNumber = '';
    this.emailAddress = '';
    this.dateOfBirth = '';
    this.cardNumber = '';
    this.shortcode = '';
    this.shortcodeExpiresAt = '';
    this.walletUrl = '';
  };

  // services
  signUp = async (verification: string) => {
    const result = await apiService.rewardsSignUp({
      code: verification,
      phoneNumber: this.phoneNumber,
      firstName: this.firstName,
      lastName: this.lastName,
      emailAddress: this.emailAddress,
      dateOfBirth: this.dateOfBirth,
      suburb: this.suburb,
    });

    if (!result?.data) {
      return false;
    }

    const {
      shortcodeExpiresAt,
      shortcode,
      cardNumber,
      firstName,
      lastName,
      status,
      walletUrl,
    } = result.data;

    this.setCardNumber(cardNumber);
    this.setShortcode(shortcode);
    this.setShortcodeExpiresAt(shortcodeExpiresAt);
    this.setFirstName(this.firstName || firstName);
    this.setLastName(this.lastName || lastName);
    this.setWalletUrl(walletUrl);

    this.dump();

    return status === 'approved';
  };

  logout = async () => {
    runInAction(() => {
      this.reset();
    });
    removeValueFromStorage(REWARDS_STORE_KEY);
  };
}
